"use client";

import { useState } from "react";
import { Icon } from "@/components/ui/Icon";
import { motion } from "framer-motion";
import { countryCodes } from "@/config/phone-codes";
import type { Locale } from "@/config/i18n";
import { tr } from "@/config/localeText";
import { RESERVATION_UI } from "./reservationFormUi";

type ReservationStep2Props = {
  onSubmit: (data: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    countryCode: string;
    company?: string;
  }) => void;
  onBack: () => void;
  isSubmitting?: boolean;
  error?: string | null;
  locale?: Locale;
};

type ValidationErrors = {
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
};

export const ReservationStep2 = ({
  onSubmit,
  onBack,
  isSubmitting = false,
  error = null,
  locale = "fr",
}: ReservationStep2Props) => {
  const t = tr(RESERVATION_UI, locale).step2;
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {},
  );
  const [submitActionError, setSubmitActionError] = useState<string | null>(
    null,
  );
  const [selectedCountry, setSelectedCountry] = useState<string>("FR");
  const [phoneNumber, setPhoneNumber] = useState<string>("");

  const validateForm = (formData: FormData): ValidationErrors => {
    const errors: ValidationErrors = {};
    const firstName = formData.get("firstName") as string;
    const lastName = formData.get("lastName") as string;
    const phone = formData.get("phone") as string;
    const email = formData.get("email") as string;

    if (!firstName?.trim()) {
      errors.firstName = t.v.firstNameRequired;
    } else if (firstName.length < 2) {
      errors.firstName = t.v.firstNameMin;
    }

    if (!lastName?.trim()) {
      errors.lastName = t.v.lastNameRequired;
    } else if (lastName.length < 2) {
      errors.lastName = t.v.lastNameMin;
    }

    if (!phone?.trim()) {
      errors.phone = t.v.phoneRequired;
    } else {
      const cleanPhoneNumber = phone.replace(/\s/g, "");
      const pattern = new RegExp(`^${countryCodes[selectedCountry].pattern}$`);
      if (!pattern.test(cleanPhoneNumber)) {
        errors.phone = t.v.phoneInvalid(countryCodes[selectedCountry].example);
      }
    }

    if (!email?.trim()) {
      errors.email = t.v.emailRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = t.v.emailInvalid;
    }

    return errors;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitActionError(null);
    const formData = new FormData(e.currentTarget);

    // Vérifier si le numéro de téléphone respecte le pattern du pays
    const cleanPhoneNumber = phoneNumber.replace(/\s/g, "");
    const pattern = new RegExp(`^${countryCodes[selectedCountry].pattern}$`);
    if (!pattern.test(cleanPhoneNumber)) {
      const phoneError = t.v.phoneInvalid(
        countryCodes[selectedCountry].example,
      );
      setValidationErrors({
        phone: phoneError,
      });
      setSubmitActionError(phoneError);
      return;
    }

    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      const summary = Object.values(errors)
        .filter((message): message is string => Boolean(message))
        .join(" ");
      setSubmitActionError(summary);
      return;
    }

    setValidationErrors({});
    onSubmit({
      firstName: formData.get("firstName") as string,
      lastName: formData.get("lastName") as string,
      phone: `${countryCodes[selectedCountry].code}${cleanPhoneNumber}`,
      email: formData.get("email") as string,
      countryCode: selectedCountry,
      company: String(formData.get("company") ?? "").trim() || undefined,
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target;
    if (submitActionError) {
      setSubmitActionError(null);
    }
    if (validationErrors[name as keyof ValidationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  return (
    <motion.div
      initial={false}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="mx-auto w-full max-w-4xl rounded-2xl border border-black/10 bg-white p-4 text-black shadow-[0_10px_32px_rgba(0,0,0,0.12)] sm:p-8"
    >
      <div className="mb-8 flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 text-gold-700/80 transition-colors hover:text-gold-800"
        >
          <Icon icon="ph:arrow-left" className="h-5 w-5" />
          <span>{t.back}</span>
        </button>
      </div>

      <h2 className="mb-6 flex items-center gap-2 text-2xl font-semibold text-black">
        <Icon icon="ph:user" className="h-6 w-6 text-gold-700" />
        <span>{t.yourInfo}</span>
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 md:gap-6">
          <div>
            <label
              htmlFor="firstName"
              className="mb-2 block text-sm font-medium text-gold-700/80"
            >
              {t.firstName}
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              required
              disabled={isSubmitting}
              onChange={handleInputChange}
              className={`peer h-12 w-full border-b ${
                validationErrors.firstName
                  ? "border-red-400"
                  : "border-black/20"
              } ks-autofill-fix bg-transparent px-4 text-black outline-none transition-all placeholder:text-transparent focus:border-gold-600 disabled:opacity-50`}
              placeholder={t.firstNamePlaceholder}
            />
            {validationErrors.firstName && (
              <p className="mt-1 text-sm text-red-400">
                {validationErrors.firstName}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="lastName"
              className="mb-2 block text-sm font-medium text-gold-700/80"
            >
              {t.lastName}
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              required
              disabled={isSubmitting}
              onChange={handleInputChange}
              className={`peer h-12 w-full border-b ${
                validationErrors.lastName ? "border-red-400" : "border-black/20"
              } ks-autofill-fix bg-transparent px-4 text-black outline-none transition-all placeholder:text-transparent focus:border-gold-600 disabled:opacity-50`}
              placeholder={t.lastNamePlaceholder}
            />
            {validationErrors.lastName && (
              <p className="mt-1 text-sm text-red-400">
                {validationErrors.lastName}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="phone"
              className="mb-2 block text-sm font-medium text-gold-700/80"
            >
              {t.phone}
            </label>
            <div className="flex gap-2">
              <select
                title={t.country}
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className={`h-12 border-b ${
                  validationErrors.phone ? "border-red-400" : "border-black/20"
                } ks-autofill-fix bg-transparent px-2 text-black outline-none transition-all focus:border-gold-600 disabled:opacity-50`}
                disabled={isSubmitting}
              >
                {Object.entries(countryCodes).map(([code, country]) => (
                  <option
                    key={code}
                    value={code}
                    className="bg-white text-black"
                  >
                    {country.flag} {country.code}
                  </option>
                ))}
              </select>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={phoneNumber}
                onChange={(e) => {
                  let value = e.target.value.replace(/[^\d]/g, "");
                  if (selectedCountry === "FR" && value.startsWith("0")) {
                    value = value.substring(1);
                  }
                  const maxLength = selectedCountry === "FR" ? 9 : 15;
                  value = value.slice(0, maxLength);

                  if (selectedCountry === "FR" && value.length > 0) {
                    const groups = [];
                    groups.push(value.slice(0, 1));
                    for (let i = 1; i < value.length; i += 2) {
                      const pair = value.slice(i, i + 2);
                      if (pair) groups.push(pair);
                    }
                    value = groups.join(" ");
                  } else {
                    value = value.replace(/(\d{2})(?=\d)/g, "$1 ").trim();
                  }

                  setPhoneNumber(value);
                  handleInputChange(e);
                }}
                disabled={isSubmitting}
                className={`peer h-12 w-full border-b ${
                  validationErrors.phone ? "border-red-400" : "border-black/20"
                } ks-autofill-fix bg-transparent px-4 text-black outline-none transition-all placeholder:text-transparent focus:border-gold-600 disabled:opacity-50`}
                placeholder={countryCodes[selectedCountry].example}
              />
            </div>
            <p className="mt-1 text-xs text-black/50">
              {t.expectedFormat(countryCodes[selectedCountry].example)}
            </p>
            {validationErrors.phone && (
              <p className="mt-1 text-sm text-red-400">
                {validationErrors.phone}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="email"
              className="mb-2 block text-sm font-medium text-gold-700/80"
            >
              {t.email}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              disabled={isSubmitting}
              onChange={handleInputChange}
              className={`peer h-12 w-full border-b ${
                validationErrors.email ? "border-red-400" : "border-black/20"
              } ks-autofill-fix bg-transparent px-4 text-black outline-none transition-all placeholder:text-transparent focus:border-gold-600 disabled:opacity-50`}
              placeholder={t.emailPlaceholder}
            />
            {validationErrors.email && (
              <p className="mt-1 text-sm text-red-400">
                {validationErrors.email}
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor="company"
              className="mb-2 block text-sm font-medium text-gold-700/80"
            >
              {t.company}
            </label>
            <input
              type="text"
              id="company"
              name="company"
              maxLength={120}
              autoComplete="organization"
              disabled={isSubmitting}
              className="ks-autofill-fix peer h-12 w-full border-b border-black/20 bg-transparent px-4 text-black outline-none transition-all placeholder:text-transparent focus:border-gold-600 disabled:opacity-50"
              placeholder={t.companyPlaceholder}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting}
            className="group relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-lg bg-primary px-8 py-3 font-medium text-black transition-colors hover:bg-gold-600 hover:text-black disabled:opacity-50 md:w-auto"
          >
            {isSubmitting ? (
              <>
                <Icon
                  icon="ph:spinner"
                  className="relative h-5 min-h-5 w-5 min-w-5 animate-spin text-black group-hover:text-black"
                />
                <span className="relative">{t.sending}</span>
              </>
            ) : (
              <>
                <Icon
                  icon="ph:check"
                  className="relative h-5 min-h-5 w-5 min-w-5 text-black group-hover:text-black"
                />
                <span className="relative">{t.confirm}</span>
              </>
            )}
          </button>
        </div>

        {(submitActionError || error) && (
          <div className="text-end text-sm text-red-500">
            {submitActionError || error}
          </div>
        )}
      </form>
    </motion.div>
  );
};
